/**
 * Colors per listing category (listing.category / course.category).
 * Fallback: brand secondary teal — #0e7490
 */
export const CATEGORY_COLOR_MAP: Record<string, string> = {
  Snorkel: "#06B6D4",
  Intro: "#8B5CF6",
  "Open Water": "#3B82F6",
  "Advanced OW": "#F59E0B",
  Rescue: "#EF4444",
  Divemaster: "#10B981",
  "Fun Dives": "#EC4899",
  Instructor: "#6366F1",
  Nitrox: "#14B8A6",
  Deep: "#1E40AF",
  "Other Specialties": "#F97316",
  Packs: "#A855F7",
  Freediving: "#0D9488",
};

/** Same palette keys as in UI (e.g. navbar) — "FUN DIVES" vs "Fun Dives" */
const CATEGORY_ALIASES: Record<string, keyof typeof CATEGORY_COLOR_MAP> = {
  "fun dives": "Fun Dives",
  "fun dive": "Fun Dives",
  "fundives": "Fun Dives",
  "advanced ow": "Advanced OW",
  "open water": "Open Water",
  "other specialties": "Other Specialties",
  freediving: "Freediving",
};

export const CATEGORY_COLOR_FALLBACK = "#0e7490";

function normalizeCategoryKey(raw: string): string {
  return raw.trim().replace(/\s+/g, " ");
}

/**
 * Hex #RRGGBB for category badge backgrounds.
 * If API text does not match (after trim / case-insensitive / aliases), returns fallback.
 */
export function getCategoryColor(category: string | null | undefined): string {
  if (!category?.trim()) return CATEGORY_COLOR_FALLBACK;

  const t = normalizeCategoryKey(category);

  if (CATEGORY_COLOR_MAP[t]) {
    return CATEGORY_COLOR_MAP[t];
  }

  const lower = t.toLowerCase();
  if (CATEGORY_ALIASES[lower]) {
    return CATEGORY_COLOR_MAP[CATEGORY_ALIASES[lower]];
  }

  const byInsensitive = Object.keys(CATEGORY_COLOR_MAP).find(
    (k) => k.toLowerCase() === lower
  );
  if (byInsensitive) {
    return CATEGORY_COLOR_MAP[byInsensitive];
  }

  return CATEGORY_COLOR_FALLBACK;
}

function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const h = hex.replace("#", "");
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
  };
}

/**
 * Same color with alpha (e.g. overlays). Use instead of hex+D9 in RN when on web.
 */
export function getCategoryColorRgba(
  category: string | null | undefined,
  alpha = 0.9
): string {
  const hex = getCategoryColor(category);
  const rgb = parseHex(hex);
  if (!rgb) return `rgba(14, 116, 144, ${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/** Agency / certification badge — single color for all agencies (not per PADI/SSI). */
export const CERTIFICATION_BADGE_BG = "rgba(0, 180, 216, 0.9)";
